/**
 * Profit Machine Dashboard Overlay
 *
 * Faithfully replicates the MT4 Panel::Draw() dashboard layout:
 *   - Header bar with product name + colored background
 *   - 2-column grid: SIGNAL/ENTRY/TARGET1 (left) | TREND/STOP/TARGET2 (right)
 *   - ✓ tick marks when TP1/TP2/SL are hit (MT4 Wingdings char 252)
 *   - 3-column footer: Script | Price | Profit
 *   - Dynamic color scheme: Green (LONG), Maroon (SHORT), SteelBlue (NEUTRAL)
 */
import React from "react";
import { ProfitMachineDashboard } from "../../indicators/built-in/profit-machine";
import "./ProfitMachineOverlay.scss";

interface ProfitMachineOverlayProps {
  dashboard: ProfitMachineDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const ProfitMachineOverlay: React.FC<ProfitMachineOverlayProps> = ({
  dashboard,
  symbol,
  visible,
  showDashboard = true,
  onToggleDashboard,
  arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    signal,
    trend,
    entry,
    stopLoss,
    target1,
    target2,
    currentPrice,
    livePL,
    status,
    tp1Hit,
    tp2Hit,
    slHit,
  } = dashboard;

  // MT4 color scheme: DarkGreen for LONG, Maroon for SHORT, SteelBlue for NEUTRAL
  const getAccentColor = (): string => {
    if (trend === "LONG") return "#006400";
    if (trend === "SHORT") return "#800000";
    return "#4682B4";
  };

  const getAccentBg = (): string => {
    if (trend === "LONG") return "rgba(76, 175, 80, 0.18)";
    if (trend === "SHORT") return "rgba(128, 0, 0, 0.25)";
    return "rgba(70, 130, 180, 0.25)";
  };

  const getValueColor = (): string => {
    if (trend === "LONG") return "#4caf50";
    if (trend === "SHORT") return "#ef5350";
    return "#90caf9";
  };

  const fmt = (v: number | null) => (v !== null ? v.toFixed(2) : "—");
  const fmtPL = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;

  const accentColor = getAccentColor();
  const accentBg = getAccentBg();
  const valueColor = getValueColor();

  return (
    <>
      {/* ─── Arrow Badges — positioned HTML labels at arrow bar coordinates ─── */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`pm-arrow-badge pm-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            // long (buy): arrow is ~20px below the candle low — put label 30px below low
            // short (sell): arrow is ~20px above the candle high — put label 44px above high
            //   (label height ~22px, so its bottom clears the arrow tip at ~20px above high)
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "Buy" : "Sell"}
        </div>
      ))}

    <div className={`pm-overlay${showDashboard ? "" : " pm-overlay--collapsed"}`}>
      {/* ─── Header (MT4: colored product title bar) ─── */}
      <div className="pm-header" style={{ background: accentColor }}>
        <span className="pm-title">Profit Machine</span>
        {showDashboard && (
          <span
            className={`pm-status ${
              status === "Active"
                ? "active"
                : status === "Stop Loss Hit"
                ? "sl-hit"
                : "target-hit"
            }`}
          >
            {status}
          </span>
        )}
        {/* Toggle switch — hides / shows body + footer */}
        <button
          className="pm-toggle-btn"
          onClick={onToggleDashboard}
          title={showDashboard ? "Hide dashboard" : "Show dashboard"}
          aria-label={showDashboard ? "Hide dashboard" : "Show dashboard"}
        >
          <span className={`pm-toggle-icon${showDashboard ? " pm-toggle-icon--open" : ""}`}>
            &#x25BE;
          </span>
        </button>
      </div>

      {/* ─── Signal Grid + Footer (hidden when collapsed) ─── */}
      {showDashboard && (
        <>
      {/* ─── Signal Grid (MT4: 2-column layout) ─── */}
      <div className="pm-body" style={{ borderLeftColor: accentColor }}>
        {/* Row 1: Signal | Trend */}
        <div className="pm-row">
          <div className="pm-cell">
            <span className="pm-label">SIGNAL</span>
            <span
              className="pm-value pm-value-box"
              style={{
                color: valueColor,
                background: accentBg,
                borderColor: accentColor,
              }}
            >
              {signal || "—"}
            </span>
          </div>
          <div className="pm-cell">
            <span className="pm-label">TREND</span>
            <span
              className="pm-value pm-value-box"
              style={{
                color: valueColor,
                background: accentBg,
                borderColor: accentColor,
              }}
            >
              {trend}
            </span>
          </div>
        </div>

        {/* Row 2: Entry | Stop */}
        <div className="pm-row">
          <div className="pm-cell">
            <span className="pm-label">ENTRY</span>
            <span className="pm-value pm-value-box">{fmt(entry)}</span>
          </div>
          <div className="pm-cell">
            <span className="pm-label">STOP</span>
            <span
              className={`pm-value pm-value-box ${slHit ? "pm-hit pm-sl-hit" : ""}`}
            >
              {fmt(stopLoss)}
              {slHit && <span className="pm-tick">✓</span>}
            </span>
          </div>
        </div>

        {/* Row 3: Target 1 | Target 2 */}
        <div className="pm-row">
          <div className="pm-cell">
            <span className="pm-label">TARGET 1</span>
            <span
              className={`pm-value pm-value-box ${tp1Hit ? "pm-hit pm-tp-hit" : ""}`}
            >
              {fmt(target1)}
              {tp1Hit && <span className="pm-tick">✓</span>}
            </span>
          </div>
          <div className="pm-cell">
            <span className="pm-label">TARGET 2</span>
            <span
              className={`pm-value pm-value-box ${tp2Hit ? "pm-hit pm-tp-hit" : ""}`}
            >
              {fmt(target2)}
              {tp2Hit && <span className="pm-tick">✓</span>}
            </span>
          </div>
        </div>

      </div>

      {/* ─── Footer (MT4: 3-column Script | Price | Profit) ─── */}
      <div className="pm-footer">
        <div className="pm-footer-row pm-footer-labels">
          <span>Script</span>
          <span>Price</span>
          <span>Profit</span>
        </div>
        <div className="pm-footer-row pm-footer-values">
          <span className="pm-script">{symbol}</span>
          <span>{currentPrice.toFixed(2)}</span>
          <span className={livePL >= 0 ? "pm-profit" : "pm-loss"}>
            {fmtPL(livePL)}
          </span>
        </div>
      </div>
        </>
      )}
    </div>
    </>
  );
};

export default ProfitMachineOverlay;
